import { useContext, useMemo, MouseEvent } from 'react';
import clsx from 'clsx';
import useOnclickOutside from 'react-cool-onclickoutside';

import { InputContext } from 'src/contexts/input.context';
import { Radio } from 'src/components/Radio';
import { P2 } from 'src/components/Typography';
import { useKeyPress } from 'src/hooks';
import { ifTrue } from 'src/tools/logic.tools';
import { render } from 'src/tools/render.tools';
import { ButtonBaseType, ID } from 'src/models';
import { capitalize } from 'src/tools/string.tools';
import { CloseButton } from 'src/components/Button';
import { UseIcon } from 'src/components/UseIcon';

import classes from './RadioList.module.scss';

export function RadioList() {
  const {
    value,
    onChange,
    onDiscard,
    isDisabled,
    isReadonly,
    onClick,
    items,
    classNames = {},
    iconId,
    onSubmit,
  } = useContext(InputContext);

  const selectedItem = useMemo(() => items?.find(i => i.id === value), [items, value]);

  useKeyPress({ onEscape: onClose }, !isDisabled);

  function onClose() {
    if (onDiscard) onDiscard();
  }

  function handleOnSubmit() {
    if (onSubmit) onSubmit();
  }

  const containerRef = useOnclickOutside(handleOnSubmit, {
    disabled: isDisabled || isReadonly,
    eventTypes: ['click'],
  });

  function handleOnChange(value: ID) {
    return function call(e: MouseEvent) {
      if (!isDisabled) {
        e.stopPropagation();

        onChange(value, true);
      }
    };
  }

  function renderButtons() {
    if (iconId) {
      return (
        <>
          <UseIcon wrapElement='button' className={classes.submit} onClick={handleOnSubmit} iconId={iconId} />
          <CloseButton className={classes.close} onClick={onClose} isSmall />
        </>
      );
    }
  }

  function renderText() {
    return <P2 className='w-100p'>{selectedItem?.label ?? capitalize(value)}</P2>;
  }

  function renderItem(item: ButtonBaseType) {
    return (
      <Radio
        key={item.id}
        label={item.label}
        isChecked={value === item.id}
        onClick={handleOnChange(item.id)}
        style={ifTrue(item.color, { color: String(item.color) })}
      />
    );
  }

  function renderRadioList() {
    return <>{render(items ?? [], renderItem)}</>;
  }

  return (
    <div
      data-testid='radio-list'
      ref={containerRef}
      className={clsx('row items-4 f-1', classNames?.container)}
      onClick={onClick}
    >
      {ifTrue(isReadonly, renderText, renderRadioList)}
      {ifTrue(iconId, renderButtons)}
    </div>
  );
}
